import * as React from 'react';
import { silentRefresh, verifyAuthToken } from 'utils/auth';
import { getStorageItem } from 'utils/ui/storage';
import logger from 'utils/log';

/** Refresh this many ms before the access token is due to expire. */
export const REFRESH_LEAD_MS = 60_000;
/** Cadence for re-checking while no valid token is present (pre-login, customer sessions) or after a failed refresh. */
export const RECHECK_INTERVAL_MS = 30_000;
/** Floor for the refresh delay so an already-expired token cannot schedule in the past. */
export const MIN_REFRESH_DELAY_MS = 1_000;

/**
 * Reads the stored auth token, treating any storage failure as "no token" rather than
 * letting it crash the refresh loop (getStorageItem throws on storage/parse errors).
 */
function readAuthToken(): unknown {
  try {
    return getStorageItem('sessionStorage', 'auth:token');
  } catch (error) {
    logger.warn('[useTokenRefresh] Failed to read auth token', { error });
    return null;
  }
}

// --- Single-owner-per-page scheduler ---------------------------------------
// In web-component (E3DRuntime) mode multiple App instances can mount on one host
// page, and they all share the SAME sessionStorage 'auth:token' + Cognito
// refresh_token. If each mount ran its own refresh loop they would fire concurrent
// silentRefresh() calls near expiry; with refresh-token rotation the first rotates
// the token and the rest POST a now-stale refresh_token (spurious 400s). To prevent
// that, the scheduler is a module-level singleton guarded by a reference count: the
// loop starts when the first hook mounts (count 0 -> 1) and is fully torn down when
// the last unmounts (count -> 0). All intermediate mounts/unmounts are no-ops.

let refCount = 0;
let timer: ReturnType<typeof setTimeout> | null = null;
// Bumped on every start AND stop. Each scheduler run captures the generation it
// belongs to; a queued timer or an in-flight silentRefresh() that resolves after a
// stop/restart sees a stale generation and bails instead of re-arming. A single
// `cancelled` boolean is not enough: a stop-while-refresh-in-flight followed by a
// restart would reset it to false and let the stale callback arm a SECOND timer
// chain alongside the new one — the very double-refresh this guard prevents.
let generation = 0;

const run = (gen: number): void => {
  if (gen !== generation) return;
  const token = readAuthToken();

  if (!verifyAuthToken(token)) {
    // Nothing to refresh yet — re-check later (cheap no-op for customer sessions).
    timer = setTimeout(() => run(gen), RECHECK_INTERVAL_MS);
    return;
  }

  const delay = Math.max(token.expiresAt * 1000 - Date.now() - REFRESH_LEAD_MS, MIN_REFRESH_DELAY_MS);
  timer = setTimeout(() => {
    void silentRefresh().then((refreshed) => {
      if (gen !== generation) return;
      // Re-arm against the new expiry on success; back off to the re-check
      // cadence on failure so we never hot-loop the token endpoint.
      timer = setTimeout(() => run(gen), refreshed ? 0 : RECHECK_INTERVAL_MS);
    });
  }, delay);
};

function startScheduler(): void {
  generation += 1;
  run(generation);
}

function stopScheduler(): void {
  // Invalidate any queued timer / in-flight refresh callback bound to the old gen.
  generation += 1;
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
}

/**
 * Keeps the dealer authenticated across the working day by silently refreshing the access
 * token shortly before it expires. Mounted once at the app root.
 *
 * The refresh loop is a SINGLE-OWNER-PER-PAGE singleton: only one scheduler runs no matter
 * how many components mount this hook. This matters in web-component (E3DRuntime) mode, where
 * several App instances can share one host page and the same sessionStorage token — running a
 * timer per instance would fire concurrent {@link silentRefresh} calls and break refresh-token
 * rotation. A module-level reference count starts the loop on the first mount and tears it down
 * when the last instance unmounts; intervening mounts/unmounts are no-ops.
 *
 * It is a no-op for sessions without a stored auth token (e.g. the customer exhibition):
 * a lightweight re-check timer runs until a token appears, then switches to expiry-driven
 * scheduling. On a failed refresh it backs off to the re-check cadence rather than stopping,
 * so a transient error does not permanently end the session — and {@link silentRefresh} works
 * on PR preview domains where the redirect-based re-auth would otherwise be required.
 */
export function useTokenRefresh(): void {
  React.useEffect(() => {
    refCount += 1;
    if (refCount === 1) startScheduler();

    return () => {
      refCount -= 1;
      if (refCount === 0) stopScheduler();
    };
  }, []);
}
